import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import DateInput from "components/common/date-picker";
import React, { useRef } from "react";
import { DownloadSimple, Trash } from "phosphor-react";
const FILE_SIZE_LIMIT = 2 * 1024 * 1024;

const validationSchema = Yup.object().shape({
  housing_expense: Yup.object().shape({
    date_of_check: Yup.date().required("Date of check is required"),
    hp_submission_reimbursement_date: Yup.date().required(
      "Submission date is required",
    ),
    hp_payment_date: Yup.date().required("Payment date is required"),
    upload_receipt: Yup.mixed().test(
      "fileSize",
      "File size must be less than or equal to 2 MB",
      (value) => !value || !value.file || value.file.size <= FILE_SIZE_LIMIT,
    ),
  }),
});

const PaymentForm = ({ onClose, selectedId, handleSubmission }) => {
  const fileInputRef = useRef(null);

  const convertFileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const formatDate = (date) => {
    if (!date) return null;

    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const renderUploadBox = (name, label, setFieldValue, values) => {
    const fileValue = name.split(".").reduce((acc, key) => acc?.[key], values);

    return (
      <div className="mb-4">
        <label className="fw-semibold mb-2 d-block">{label}</label>

        <div className="border border-warning rounded p-4 text-center bg-light h-100">
          <span>Drag & drop or </span>

          <label className="text-warning fw-bold" style={{ cursor: "pointer" }}>
            choose file
            <input
              ref={fileInputRef}
              type="file"
              hidden
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;

                setFieldValue(name, {
                  file: file,
                  url: null,
                  name: file.name,
                });
              }}
            />
          </label>

          <div className="text-muted small mt-1">(Max. 2 MB)</div>

          {fileValue && (
            <div className="mt-2 d-flex justify-content-center">
              <Trash
                size={20}
                className="cursor-pointer opacity-50"
                onClick={() => setFieldValue(name, null)}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Formik
      initialValues={{
        housing_expense: {
          date_of_check: "",
          hp_submission_reimbursement_date: "",
          hp_payment_date: "",
          upload_receipt: selectedId?.upload_receipt
            ? {
                file: null,
                url: selectedId.upload_receipt,
                name: selectedId.upload_receipt_file_name,
              }
            : null,
        },
      }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        const uploadReceipt = values.housing_expense.upload_receipt;

        const payload = {
          date_of_check: formatDate(values.housing_expense.date_of_check),
          hp_submission_reimbursement_date: formatDate(
            values.housing_expense.hp_submission_reimbursement_date,
          ),
          hp_payment_date: formatDate(values.housing_expense.hp_payment_date),
          upload_receipt: uploadReceipt?.file
            ? await convertFileToBase64(uploadReceipt.file)
            : uploadReceipt?.url || null,
          upload_receipt_file_name:
            uploadReceipt?.file?.name || uploadReceipt?.name || null,
          is_payment_confirmed: true,
        };

        handleSubmission(selectedId, payload);
      }}
    >
      {({ setFieldValue, values }) => (
        <Form className="form-main mt-4">
          <div className="form-info">
            <p style={{ display: "flex", alignItems: "center" }}>
              <i className="icon-info" />
              <span style={{ marginLeft: "5px" }}>
                Please issue the payment via{" "}
                <a
                  href="https://www.bill.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="highlight">Bill.com</span>
                </a>
              </span>
            </p>

            <p>
              <i className="icon-info" />
              If you have issued the payment, please upload the receipt below.
            </p>
          </div>

          <DateInput
            label="Date of Check"
            name="housing_expense.date_of_check"
            value={values.housing_expense.date_of_check}
            handleChange={(date) =>
              setFieldValue("housing_expense.date_of_check", date)
            }
            error={
              <ErrorMessage
                className="error-message"
                component="p"
                name="housing_expense.date_of_check"
              />
            }
          />

          <DateInput
            label="HP Submission Reimbursement Date"
            name="housing_expense.hp_submission_reimbursement_date"
            value={values.housing_expense.hp_submission_reimbursement_date}
            handleChange={(date) =>
              setFieldValue(
                "housing_expense.hp_submission_reimbursement_date",
                date,
              )
            }
            error={
              <ErrorMessage
                className="error-message"
                component="p"
                name="housing_expense.hp_submission_reimbursement_date"
              />
            }
          />

          <DateInput
            label="HP Payment Date"
            name="housing_expense.hp_payment_date"
            value={values.housing_expense.hp_payment_date}
            handleChange={(date) =>
              setFieldValue("housing_expense.hp_payment_date", date)
            }
            error={
              <ErrorMessage
                className="error-message"
                component="p"
                name="housing_expense.hp_payment_date"
              />
            }
          />

          {renderUploadBox(
            "housing_expense.upload_receipt",
            "Upload Receipt",
            setFieldValue,
            values,
          )}

          <ErrorMessage
            name="housing_expense.upload_receipt"
            component="p"
            style={{ color: "red", fontSize: "0.8rem" }}
          />

          {values.housing_expense.upload_receipt && (
            <p className="text-sm text-gray-600 mt-2">
              Selected: {values.housing_expense.upload_receipt.name}
            </p>
          )}

          {values.housing_expense.upload_receipt?.url && (
            <div className="mt-2">
              <a
                href={values.housing_expense.upload_receipt.url}
                download={values.housing_expense.upload_receipt.name}
                className="text-blue-500 cursor-pointer flex items-center gap-1"
              >
                <DownloadSimple size={25} />
                {values.housing_expense.upload_receipt.name}
              </a>
            </div>
          )}

          <div className="action gap-2">
            <button
              type="button"
              className="secondary-btn w-50"
              onClick={onClose}
            >
              Close
            </button>
            <button type="submit" className="primary-btn w-50 fs-6">
              Confirm Payment Issuance
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default PaymentForm;
